/**
 * VoteResult — shown when voting resolution is reached.
 * Displays final tallies, winner highlight, and resolution summary.
 */
import React from 'react';
import type { VoteResult as VoteResultType, VoteOption, VoteResolution } from '@/types/pm-types';

interface VoteResultProps {
  results: VoteResultType[];
  options: VoteOption[];
  resolution: VoteResolution;
  resolvedAt?: string;
  accentColor?: string;
}

export const VoteResultDisplay: React.FC<VoteResultProps> = ({
  results,
  options,
  resolution,
  resolvedAt,
  accentColor,
}) => {
  const winnerResult = results.reduce((best, r) =>
    r.approveCount > best.approveCount ? r : best,
    results[0]
  );
  const winnerOption = options.find((o) => o.id === winnerResult?.optionId);

  return (
    <div style={{
      width: '100%',
      padding: 12,
      borderRadius: 8,
      background: '#F0FDF4',
      border: '1px solid #BBF7D0',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 8,
      }}>
        <span style={{ fontSize: 16 }}>&#x2705;</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#166534' }}>
          Voting Complete
        </span>
        {resolvedAt && (
          <span style={{ fontSize: 10, color: '#6B7280', marginLeft: 'auto' }}>
            {new Date(resolvedAt).toLocaleString()}
          </span>
        )}
      </div>

      {/* Winner highlight */}
      {winnerOption && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px',
          marginBottom: 8,
          borderRadius: 6,
          background: 'white',
          border: `2px solid ${accentColor || '#F59E0B'}`,
        }}>
          <span style={{ fontSize: 14 }}>&#x1F451;</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#1F2937' }}>
            {winnerOption.label}
          </span>
          <span style={{ fontSize: 11, color: '#6B7280', marginLeft: 'auto' }}>
            {winnerResult.approveCount} approvals
          </span>
        </div>
      )}

      {/* Per-option results */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {results.map((r) => {
          const option = options.find((o) => o.id === r.optionId);
          if (!option) return null;
          const isWinner = r.optionId === winnerResult?.optionId;
          return (
            <div key={r.optionId} style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '4px 8px',
              borderRadius: 4,
              background: isWinner ? '#ECFDF5' : 'transparent',
            }}>
              <span style={{
                fontSize: 10,
                fontWeight: 500,
                padding: '1px 6px',
                borderRadius: 3,
                background: r.approveCount > r.vetoCount ? '#DCFCE7' : r.vetoCount > 0 ? '#FEE2E2' : '#F3F4F6',
                color: r.approveCount > r.vetoCount ? '#166534' : r.vetoCount > 0 ? '#991B1B' : '#6B7280',
              }}>
                {r.approveCount > r.vetoCount ? 'Approved' : r.vetoCount > 0 ? 'Vetoed' : 'Pending'}
              </span>
              <span style={{ fontSize: 12, color: '#374151', flex: 1 }}>
                {option.label}
              </span>
              <span style={{ fontSize: 10, color: '#9CA3AF' }}>
                {r.approveCount}&#x2191; {r.vetoCount}&#x2193;
              </span>
            </div>
          );
        })}
      </div>

      {/* Resolution summary */}
      <div style={{
        marginTop: 8,
        paddingTop: 8,
        borderTop: '1px solid #D1FAE5',
        fontSize: 10,
        color: '#6B7280',
      }}>
        Resolution: {resolution.type.charAt(0).toUpperCase() + resolution.type.slice(1)}
      </div>
    </div>
  );
};

VoteResultDisplay.displayName = 'VoteResultDisplay';
